import { useQuery } from '@hooks/reactQuery';

import { getCountersGroup, getKpiGroup, getKpiGroupLineType } from '@api/kpi';

import { buildFlatTree } from '@utils/treeFormatter/treeFormatter';
import { sortNodesByCheckedCount } from '@components/Kpi/Parameters/ParametersKpiSelector/TreeGroupSelectWrapper/TreeGroupSelect/utils';

import { IFlatMoTreeNode, IMoTreeInitial } from '@interfaces/components/tree';
import { IKpiTreeGroup } from '@interfaces/kpi/dto/kpiDTO';

import { EUserRoles, EVendorsValues, TVendorsValues } from '@enums/common';
import { ETreeTypeNode } from '@enums/tree';

import { refetchOptions } from '@constants/common';
import { COUNTERS_GROUP, KPI_GROUP } from '@constants/queryKeys';

const convertToMoTreeInitial = (kpiTreeGroup: IKpiTreeGroup): IMoTreeInitial[] => {
	const convertNode = (node: IKpiTreeGroup['items'][number]): IMoTreeInitial => {
		return {
			categoryId: '',
			dbId: node.id,
			name: node.title,
			tags: node.tags,
			type: node?.items
				? { name: ETreeTypeNode.object, title: ETreeTypeNode.object }
				: { name: ETreeTypeNode.parameter, title: ETreeTypeNode.parameter },
			subNodes: node.items ? node.items.map(convertNode) : undefined,
		};
	};

	return kpiTreeGroup.items.map(convertNode);
};

const wrapKpiData = (data: IKpiTreeGroup, wrapNode: string): IKpiTreeGroup => {
	return {
		items: [{
			title: wrapNode,
			items: data.items, id: '',
			tags: [],
		}],
	};
};

const HEAD_NODE = 'All';

interface IUseQueryKpiGroup {
	vendor?: TVendorsValues;
	isWithMaxSelection?: boolean;
	isLineType?: boolean;
}

interface IUseQueryKpiGroupReturn {
	kpiGroupData: IFlatMoTreeNode[];
	countersGroupData: IFlatMoTreeNode[];
	isLoadingKpiGroup: boolean;
}

export const useQueryKpiGroup = ({
	vendor,
	isWithMaxSelection,
	isLineType,
}: IUseQueryKpiGroup): IUseQueryKpiGroupReturn => {
	const getKpiGroupFn = isLineType ? getKpiGroupLineType : getKpiGroup;

	const {
		data: kpiData,
		isInitialLoading: isLoadingKpiSettings,
	} = useQuery({
		queryKey: [KPI_GROUP, vendor],
		queryFn: async () => {
			const data = await getKpiGroupFn(vendor!);

			if (!data) {
				return [];
			}

			const processedData = isLineType
				? { items: data.items.filter(item => item?.items?.length > 0) }
				: data;

			const treeData = convertToMoTreeInitial(
				isWithMaxSelection ? processedData : wrapKpiData(processedData, HEAD_NODE),
			);
			const flatTree = buildFlatTree({ data: treeData });

			return sortNodesByCheckedCount(flatTree);
		},
		allowedRoles: [
			EUserRoles.networkOperatorKpiViewer,
			EUserRoles.networkOperatorKpiUpdater,
		],
		enabled: [vendor],
		...refetchOptions,
		staleTime: 0,
		cacheTime: 0,
	});

	const {
		data: countersData,
		isInitialLoading: isLoadingCountersSettings,
		isFetching: isFetchingCountersSettings,
	} = useQuery({
		queryKey: [COUNTERS_GROUP, vendor],
		queryFn: async () => {
			return getCountersGroup(vendor!).then(async (data) => {
				const result: IFlatMoTreeNode[] = await new Promise((resolve) => {
					const temporaryFilteredItems = data.items.filter((item: IKpiTreeGroup['items'][number]) =>
						!item.title.startsWith('Unknown (temporary)'),
					);

					const treeData = convertToMoTreeInitial(
						isWithMaxSelection ?
							{ items: temporaryFilteredItems }
							: wrapKpiData({ items: temporaryFilteredItems }, HEAD_NODE),
					);

					const result = buildFlatTree({ data: treeData });
					const sorted = sortNodesByCheckedCount(result);

					resolve(sorted);
				});

				return result;
			});
		},
		allowedRoles: [
			EUserRoles.networkOperatorKpiViewer,
			EUserRoles.networkOperatorKpiUpdater,
		],
		enabled: [vendor, vendor === EVendorsValues.oran],
		...refetchOptions,
	});

	return {
		kpiGroupData: kpiData || [],
		countersGroupData: countersData || [],
		isLoadingKpiGroup: (
			isLoadingKpiSettings ||
			isLoadingCountersSettings ||
			isFetchingCountersSettings
		),
	};
};
